"use client"

import * as React from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { cn } from '../../../lib/utils'
import { Button } from '../button'
import { Badge } from '../badge'
import { Checkbox } from '../checkbox'
import type { GroupSectionProps } from "./types"
import type { DataTableColumnDef } from "../data-table/types"

/**
 * Get the accessor key from a column definition
 */
export function getAccessorKey<TData>(
  column: DataTableColumnDef<TData, unknown>
): string | undefined {
  if ("accessorKey" in column) {
    return column.accessorKey as string
  }
  return undefined
}

/**
 * Render a cell value from column definition
 */
export function renderCellValue<TData>(
  column: DataTableColumnDef<TData, unknown>,
  row: TData,
  rowIndex: number
): React.ReactNode {
  // If column has a cell renderer, use it
  if (column.cell) {
    if (typeof column.cell === "function") {
      const accessorKey = getAccessorKey(column)
      // Create a minimal context object similar to TanStack Table
      const context = {
        row: {
          original: row,
          index: rowIndex,
          getValue: (key: string) => (row as Record<string, unknown>)[key],
        },
        getValue: () => {
          return accessorKey ? (row as Record<string, unknown>)[accessorKey] : undefined
        },
      }
      return column.cell(context as never)
    }
    return column.cell as React.ReactNode
  }

  // Fall back to accessor key
  const accessorKey = getAccessorKey(column)
  if (accessorKey) {
    const value = (row as Record<string, unknown>)[accessorKey]
    return value != null ? String(value) : ""
  }

  return null
}

/**
 * Get the checkbox checked state for group selection
 */
function getGroupCheckboxState(
  allSelected: boolean,
  someSelected: boolean
): boolean | "indeterminate" {
  if (allSelected) return true
  if (someSelected) return "indeterminate"
  return false
}

/**
 * GroupSection component - renders a group header row and data rows
 * Used inside a single Table in GroupedDataView
 */
export function GroupSection<TData>({
  group,
  columns,
  stickyColumns = 0,
  enableSelection,
  selection,
  onSelectionChange,
  getRowId,
  onRowClick,
  onRowDoubleClick,
  onToggleExpand,
  formatGroupLabel,
  rowIndexOffset,
}: Omit<GroupSectionProps<TData>, "enableSorting" | "sorting" | "onSortChange">) {
  // Get all row IDs in this group
  const groupRowIds = React.useMemo(
    () => group.items.map((item, idx) => getRowId(item, rowIndexOffset + idx)),
    [group.items, getRowId, rowIndexOffset]
  )

  // Check if all rows in group are selected
  const allSelected = groupRowIds.length > 0 && groupRowIds.every((id) => selection.has(id))
  const someSelected = groupRowIds.some((id) => selection.has(id))

  // Handle group checkbox change
  const handleGroupCheckboxChange = (checked: boolean | "indeterminate") => {
    onSelectionChange(groupRowIds, checked === true)
  }

  // Handle individual row checkbox
  const handleRowCheckboxChange = (rowId: string, checked: boolean | "indeterminate") => {
    onSelectionChange([rowId], checked === true)
  }

  // Filter out selection column from displayed columns if enableSelection is false
  const displayColumns = React.useMemo(() => {
    if (enableSelection) {
      return columns
    }
    return columns.filter((col) => col.id !== "select")
  }, [columns, enableSelection])

  // Check if selection column is present
  const hasSelectionColumn = enableSelection && displayColumns.some(col => col.id === "select")

  return (
    <>
      {/* Group Header Row */}
      <tr className="bg-muted/50 hover:bg-muted/70">
        {displayColumns.map((column, colIndex) => {
          const accessorKey = getAccessorKey(column)
          const columnId = column.id || accessorKey || `col-${colIndex}`
          const isSticky = colIndex < stickyColumns
          const isSelectionColumn = column.id === "select"

          // Calculate left offset for sticky columns
          const stickyLeft = isSticky
            ? colIndex === 0
              ? 0
              : colIndex === 1
                ? 40 // Width of checkbox column
                : undefined
            : undefined

          // Selection column cell - just the chevron
          if (isSelectionColumn) {
            return (
              <td
                key={columnId}
                className={cn(
                  "p-2 align-middle whitespace-nowrap bg-muted/50",
                  column.meta?.cellClassName,
                  isSticky && "sticky z-20"
                )}
                style={stickyLeft !== undefined ? { left: stickyLeft } : undefined}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-background/50"
                  onClick={onToggleExpand}
                >
                  {group.isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </td>
            )
          }

          // First data column (or first column if no selection) - contains group label
          if ((hasSelectionColumn && colIndex === 1) || (!hasSelectionColumn && colIndex === 0)) {
            return (
              <td
                key={columnId}
                className={cn(
                  "p-2 align-middle whitespace-nowrap bg-muted/50",
                  column.meta?.cellClassName,
                  isSticky && "sticky z-20"
                )}
                style={stickyLeft !== undefined ? { left: stickyLeft } : undefined}
              >
                <div
                  onClick={onToggleExpand}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  {!hasSelectionColumn && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-background/50"
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleExpand()
                      }}
                    >
                      {group.isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  )}

                  {enableSelection && (
                    <Checkbox
                      checked={getGroupCheckboxState(allSelected, someSelected)}
                      onCheckedChange={handleGroupCheckboxChange}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select all in ${group.groupLabel}`}
                    />
                  )}

                  <span className="font-medium text-foreground whitespace-nowrap">
                    {formatGroupLabel
                      ? formatGroupLabel(group.groupValue, group.items)
                      : group.groupLabel}
                  </span>

                  <Badge variant="secondary" className="font-normal text-xs">
                    {group.items.length}
                  </Badge>
                </div>
              </td>
            )
          }

          // Other cells are empty
          return (
            <td
              key={columnId}
              className="p-2 align-middle whitespace-nowrap bg-muted/50"
            />
          )
        })}
      </tr>

      {/* Data Rows */}
      {group.isExpanded &&
        group.items.map((row, rowIndex) => {
          const globalRowIndex = rowIndexOffset + rowIndex
          const rowId = getRowId(row, globalRowIndex)
          const isSelected = selection.has(rowId)

          return (
            <tr
              key={rowId}
              data-state={isSelected ? "selected" : undefined}
              onClick={onRowClick ? (e) => onRowClick(row, e) : undefined}
              onDoubleClick={
                onRowDoubleClick ? (e) => onRowDoubleClick(row, e) : undefined
              }
              className={cn(
                "hover:bg-muted/50 data-[state=selected]:bg-muted transition-colors",
                onRowClick && "cursor-pointer"
              )}
            >
              {displayColumns.map((column, colIndex) => {
                const accessorKey = getAccessorKey(column)
                const columnId = column.id || accessorKey || `col-${colIndex}`
                const isSelectionColumn = column.id === "select"
                const isSticky = colIndex < stickyColumns

                // Calculate left offset for sticky columns
                const stickyLeft = isSticky
                  ? colIndex === 0
                    ? 0
                    : colIndex === 1
                      ? 40 // Width of checkbox column
                      : undefined
                  : undefined

                return (
                  <td
                    key={columnId}
                    className={cn(
                      "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5",
                      column.meta?.cellClassName,
                      column.meta?.align === "center" && "text-center",
                      column.meta?.align === "right" && "text-right",
                      // A frozen cell has to be opaque, and it must match the
                      // table container's tier — which is now `bg-panel`.
                      isSticky && "sticky bg-panel z-10"
                    )}
                    style={stickyLeft !== undefined ? { left: stickyLeft } : undefined}
                  >
                    {isSelectionColumn ? (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleRowCheckboxChange(rowId, checked)
                        }
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select row ${rowId}`}
                      />
                    ) : (
                      renderCellValue(column, row, globalRowIndex)
                    )}
                  </td>
                )
              })}
            </tr>
          )
        })}
    </>
  )
}
